import React, { useCallback, useEffect } from "react";
import { InlineNotification, Link as TextLink, useToast } from "@familyzone/component-library";
import { AttributeMapping, MergeConfig, ProviderData, SISProvider } from "./SisIntegration";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Text,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  Select,
} from "@chakra-ui/react";
import { MergeOptions, UserMergeKey } from "../SyncConfig/ConfigForm/MergeOptions";
import { extractSyncError } from "../../../utils/api/SyncApi";
import { saveSisSync } from "./mapSyncNodeToSISProvider";
import { Controller, useForm } from "react-hook-form";
import { ConfigFormLabel } from "../SyncConfig/ConfigForm/ConfigFormLabel";
import { TextFieldControl } from "../SyncConfig/ConfigForm/TextFieldControl";
import { CheckBoxControl } from "../SyncConfig/ConfigForm/CheckboxControl";
import NuMultiSelect from "../../../utils/NuMultiSelect";
import { get } from "shades";
import {
  AttributeConfig,
  YearAttributes,
  defaultYearAttributes,
  YearAttributesToInput,
  StoredOrDefaultYearAttributes,
} from "./AttributeConfig";
import { colors } from "../../../theme";

interface Props {
  rowToEdit: SISProvider | null;
  show: boolean;
  handleHide: () => void;
  handleSubmit: () => void;
  multipleSyncsConfigured?: boolean;
}

export interface WondeInputs {
  integration_type: string;
  name: string;
  domain: string;
  district_id: string;
  access_token: string;
  username_key: string;
  teacher_username_key: string;
  email_key: string;
  user_match: string;
  user_email_key: string;
  classname_keys: string;
  sync_classrooms: boolean;
  sync_parent_data: boolean;
  guardian_email_key: string;
  guardian_types: string[];
  user_merge_priority: number;
  user_merge_key: UserMergeKey;
  user_merge_expression?: string;
  user_merge_attach_only: boolean;
  sync_year_attributes: boolean;
  year_attribute_mappings: YearAttributes;
  respect_enrolment_date?: boolean;
  respect_sessions?: boolean;
  id?: string;
  enabled: boolean;
  excluded_schools_list: string[];
  type: "wonde";
}

export const SisWondeForm: React.FC<Props> = ({
  rowToEdit,
  show,
  handleHide,
  handleSubmit: handleSubmitExternal,
  multipleSyncsConfigured = false,
}) => {
  const { successToast, errorToast } = useToast();
  const classname_keys_subject_code = "class.subject.data.code - class.description";
  const classname_keys_class_code = "class.code - class.description";

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
    setError,
    clearErrors,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<WondeInputs>({
    defaultValues: {
      integration_type: "Other",
      name: "",
      domain: "api.wonde.com",
      district_id: "",
      access_token: "",
      username_key: "mis_id",
      teacher_username_key: "",
      email_key: "work",
      user_match: "username",
      user_email_key: "primary",
      classname_keys: classname_keys_subject_code,
      sync_classrooms: true,
      sync_parent_data: false,
      guardian_email_key: "work",
      guardian_types: [],
      user_merge_priority: 10,
      user_merge_expression: undefined,
      user_merge_key: UserMergeKey.Email,
      user_merge_attach_only: false,
      sync_year_attributes: true,
      year_attribute_mappings: defaultYearAttributes,
      respect_enrolment_date: false,
      respect_sessions: false,
      id: undefined,
      enabled: true,
      excluded_schools_list: [],
      type: "wonde",
    },
  });

  const syncClassroomDataWatch = watch("sync_classrooms");
  const syncParentDataWatch = watch("sync_parent_data");
  const syncYearAttributesWatch = watch("sync_year_attributes");
  const mergeKey = watch("user_merge_key");

  useEffect(() => {
    if (rowToEdit) {
      reset({
        integration_type: "Other",
        name: rowToEdit?.name || "",
        domain: rowToEdit?.api_configuration.domain || "api.wonde.com",
        district_id: rowToEdit?.api_configuration.district_id || "",
        access_token: rowToEdit?.api_configuration.access_token || "",
        username_key: rowToEdit?.api_configuration.username_key || "mis_id",
        teacher_username_key: rowToEdit?.api_configuration.teacher_username_key || "",
        email_key: rowToEdit?.api_configuration.email_key || "work",
        user_match: rowToEdit?.api_configuration.user_match || "username",
        user_email_key: rowToEdit?.api_configuration.user_email_key || "primary",
        classname_keys: rowToEdit?.api_configuration.classname_keys || classname_keys_subject_code,
        sync_classrooms: rowToEdit?.sync_classrooms !== undefined && rowToEdit?.sync_classrooms !== null ? rowToEdit.sync_classrooms : true,
        sync_parent_data: rowToEdit?.sync_parents || false,
        guardian_email_key: rowToEdit?.api_configuration.guardian_email_key || "email",
        guardian_types:
          rowToEdit?.api_configuration.guardian_roles && rowToEdit?.api_configuration.guardian_roles.length > 0
            ? rowToEdit.api_configuration.guardian_roles
            : [],
        user_merge_priority: rowToEdit.merge_config?.priority || 10,
        user_merge_expression: rowToEdit.merge_config?.computed?.expression || undefined,
        user_merge_key: rowToEdit.merge_config?.computed
          ? UserMergeKey.Computed
          : rowToEdit.merge_config?.email?.stripped
          ? UserMergeKey.EmailStripped
          : UserMergeKey.Email || UserMergeKey.Email,
        user_merge_attach_only: rowToEdit.merge_config?.attachOnly || false,

        sync_year_attributes: rowToEdit.sync_attributes?.years?.enabled ?? true,
        year_attribute_mappings: StoredOrDefaultYearAttributes(rowToEdit.sync_attributes?.years?.mappings),
        id: rowToEdit.id,
        enabled: rowToEdit ? rowToEdit.enabled : true,
        excluded_schools_list: rowToEdit.excluded_schools_list || [],
        type: "wonde",
      });
    }
  }, [reset, rowToEdit, setValue, show]);

  const onSubmit = useCallback(
    (input: WondeInputs) => {
      return new Promise<void>((resolve, reject) => {
        // Cross-field validation: at least one of parents/classrooms must be enabled
        if (!input.sync_parent_data && !input.sync_classrooms) {
          const msg = "Please select either parent data, classroom data, or both to be synced.";
          setError("sync_parent_data", { type: "validate", message: msg });
          setError("sync_classrooms", { type: "validate", message: msg });
          resolve();
          return;
        }

        // Build mappings and validate duplicates (single pass via YearAttributesToInput)
        const { mappings, duplicateKeys } = YearAttributesToInput(input.year_attribute_mappings);
        if (duplicateKeys.size > 0) {
          setError(`sync_year_attributes`, {
            type: "validate",
            message: "Cannot have duplicate values across inputs.",
          });
          resolve();
          return;
        }

        const mergeConfig: MergeConfig = {
          priority: input.user_merge_priority,
          attachOnly: input.user_merge_attach_only,
        };
        if (input.user_merge_key === UserMergeKey.Computed) {
          mergeConfig.computed = {
            expression: input.user_merge_expression || "",
          };
        } else {
          mergeConfig.email = {
            stripped: input.user_merge_key === UserMergeKey.EmailStripped,
          };
        }

        const yearAttributes: AttributeMapping = {
          enabled: input.sync_year_attributes,
          mappings: mappings,
        };

        const apiData: ProviderData = {
          type: "wonde",
          enabled: input.enabled,
          sync_parents: input.sync_parent_data,
          sync_parent_types: input.guardian_types.map((type) => type.toLowerCase()),
          sync_classrooms: input.sync_classrooms,
          respect_enrolment_date: false,
          respect_sessions: false,
          integration_type: input.integration_type,
          excluded_schools_list: input.excluded_schools_list,
          name: input.name,
          api_configuration: {
            domain: input.domain,
            district_id: input.district_id,
            access_token: input.access_token,
            username_key: input.username_key,
            teacher_username_key: input.teacher_username_key,
            email_key: input.email_key,
            user_match: input.user_match,
            user_email_key: input.user_email_key,
            classname_keys: input.classname_keys,
            guardian_email_key: input.guardian_email_key,
            guardian_roles: input.guardian_types,
          },
          merge_config: mergeConfig,
          sync_attributes: {
            years: yearAttributes,
          },
        };

        const defaultMsg = "An unknown error occurred while trying to save configuration.";
        const onSuccess = () => {
          successToast({ title: "Saved Wonde configuration" });
          handleSubmitExternal();
          resolve();
        };
        const onFailure = (msg: string) => {
          errorToast({ title: "Failed to save Wonde configuration", description: msg });
          reject(msg);
        };

        // SM-API: POST /api/syncs (create) or PATCH /api/syncs/{id} (update).
        saveSisSync(apiData, input.id)
          .then(onSuccess)
          .catch((e) => onFailure(extractSyncError(e, defaultMsg)));
      });
    },
    [setError, successToast, handleSubmitExternal, errorToast]
  );

  const handleHideClick = () => {
    handleHide();
  };

  return (
    <Modal isOpen={show} onClose={handleHideClick} isCentered scrollBehavior={"inside"}>
      <ModalOverlay />
      {/*https://github.com/orgs/react-hook-form/discussions/8622*/}
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalContent maxWidth={"800px"} maxHeight={"80%"}>
          <ModalHeader
            sx={{
              display: "flex",
              flexDir: "row",
              justifyContent: "space-between",
            }}
          >
            {!rowToEdit ? "Add Wonde Sync" : "Edit Wonde Sync"}
            <img
              alt="Close modal button"
              className="modal-close-icon"
              width="13"
              height="13"
              src="/static/images/icon_modal_close.svg"
              onClick={handleHideClick}
            />
          </ModalHeader>
          <ModalBody
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              overflow: "auto",
              flex: 1,
            }}
          >
            {rowToEdit?.read_error && (
              <InlineNotification
                status="error"
                notificationTitle={"This configuration could not be read"}
                notificationDescription={`${rowToEdit.read_error}. The fields below show defaults, not the stored values — saving will replace the entire configuration.`}
              />
            )}
            <TextLink isExternal textAlign={"center"} href={"https://help.linewize.com/hc/en-gb/articles/5733216183324"}>
              Configuring Wonde integration Guide
            </TextLink>
            <Text textAlign={"center"}>
              {"Provide your Wonde account details below to integrate your school system with Linewize Filter"}
            </Text>

            <Box sx={{ display: "flex", flexDirection: "column", padding: "24px", gap: "16px", fontSize: "14px" }}>
              <TextFieldControl
                styleType={"column"}
                fieldProps={{
                  name: "name",
                  label: "Name",
                  tooltip: "The name of this Wonde Sync Configuration",
                  control: control,
                  errors: errors,
                  testId: "name-input",
                  rules: {
                    required: "A name is required.",
                  },
                }}
              />

              <TextFieldControl
                styleType={"column"}
                fieldProps={{
                  name: "access_token",
                  label: "Access Token",
                  tooltip: "The access token for your Wonde account",
                  control: control,
                  errors: errors,
                  testId: "access_token-input",
                  rules: {
                    required: "An access token is required.",
                  },
                }}
              />

              <Controller
                control={control}
                name="domain"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <FormControl
                    isInvalid={!!error}
                    sx={{
                      display: "flex",
                      flexDir: "column",
                      minHeight: "40px",
                      gap: "4px",
                    }}
                  >
                    <ConfigFormLabel label={"Domain"} tooltip={"The domain for your Wonde account."} />
                    <Select
                      value={value || ""}
                      onChange={(e) => onChange(e.target.value)}
                      data-testid={"domainKey-select"}
                      iconSize={"0"}
                      size="lg"
                      variant={"outline"}
                      sx={{
                        width: "100%",
                        height: "40px",
                        backgroundColor: "white",
                        borderRadius: "6px",
                        border: `1px solid ${colors.neutrals["30"]}`,
                        paddingLeft: "16px",
                      }}
                    >
                      <option value="api.wonde.com">api.wonde.com</option>
                      <option value="api-ap-southeast-2.wonde.com">api-ap-southeast-2.wonde.com</option>
                      <option value="api-af-south-1.wonde.com">api-af-south-1.wonde.com</option>
                    </Select>
                    <FormErrorMessage>{error && error.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              <TextFieldControl
                styleType={"column"}
                fieldProps={{
                  name: "district_id",
                  label: "School ID",
                  tooltip: "The district identifier for your Wonde account",
                  control: control,
                  errors: errors,
                  testId: "district_id-input",
                }}
              />

              <Controller
                control={control}
                name="user_email_key"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <FormControl
                    isInvalid={!!error}
                    sx={{
                      display: "flex",
                      flexDir: "column",
                      minHeight: "40px",
                      gap: "4px",
                    }}
                  >
                    <ConfigFormLabel label={"SIS student email field"} tooltip={"The field to use as the email for students."} />
                    <Select
                      value={value || ""}
                      onChange={(e) => onChange(e.target.value)}
                      data-testid={"userEmailKey-select"}
                      iconSize={"0"}
                      size="lg"
                      variant={"outline"}
                      sx={{
                        width: "100%",
                        height: "40px",
                        backgroundColor: "white",
                        borderRadius: "6px",
                        border: `1px solid ${colors.neutrals["30"]}`,
                        paddingLeft: "16px",
                      }}
                    >
                      <option value="work">work</option>
                      <option value="email">email</option>
                      <option value="primary">primary</option>
                      <option value="home">home</option>
                    </Select>
                    <FormErrorMessage>{error && error.message}</FormErrorMessage>
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="username_key"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <FormControl
                    isInvalid={!!error}
                    sx={{
                      display: "flex",
                      flexDir: "column",
                      minHeight: "40px",
                      gap: "4px",
                    }}
                  >
                    <ConfigFormLabel label={"SIS student username field"} tooltip={"The field to use as the username for students."} />
                    <Select
                      value={value || ""}
                      onChange={(e) => onChange(e.target.value)}
                      data-testid={"usernameKey-select"}
                      iconSize={"0"}
                      size="lg"
                      variant={"outline"}
                      sx={{
                        width: "100%",
                        height: "40px",
                        backgroundColor: "white",
                        borderRadius: "6px",
                        border: `1px solid ${colors.neutrals["30"]}`,
                        paddingLeft: "16px",
                      }}
                    >
                      <option value="active_directory_username">active_directory_username</option>
                      <option value="mis_id">mis_id</option>
                      <option value="username">username</option>
                    </Select>
                    <FormErrorMessage>{error && error.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              <CheckBoxControl
                fieldProps={{
                  name: "sync_classrooms",
                  label: "Sync Classroom Data",
                  tooltip: "When this is ticked, syncs include classrooms and classroom relationships",
                  control: control,
                  errors: errors,
                  rules: {
                    validate: (v: boolean) =>
                      v || syncParentDataWatch || "Please select either parent data, classroom data, or both to be synced.",
                  },
                }}
              />

              {syncClassroomDataWatch && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                    paddingLeft: "20px",
                  }}
                >
                  <Controller
                    control={control}
                    name="classname_keys"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <FormControl
                        isInvalid={!!error}
                        sx={{
                          display: "flex",
                          flexDir: "column",
                          minHeight: "40px",
                          gap: "4px",
                        }}
                      >
                        <ConfigFormLabel label={"Classname field"} tooltip={"The field to use as the classname for classrooms."} />
                        <Select
                          value={value || ""}
                          onChange={(e) => onChange(e.target.value)}
                          data-testid={"classnameKeys-select"}
                          iconSize={"0"}
                          size="lg"
                          variant={"outline"}
                          sx={{
                            width: "100%",
                            height: "40px",
                            backgroundColor: "white",
                            borderRadius: "6px",
                            border: `1px solid ${colors.neutrals["30"]}`,
                            paddingLeft: "16px",
                          }}
                        >
                          <option value={classname_keys_subject_code}>{classname_keys_subject_code}</option>
                          <option value={classname_keys_class_code}>{classname_keys_class_code}</option>
                        </Select>
                        <FormErrorMessage>{error && error.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  />
                  <Controller
                    control={control}
                    name="teacher_username_key"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <FormControl
                        isInvalid={!!error}
                        sx={{
                          display: "flex",
                          flexDir: "column",
                          minHeight: "40px",
                          gap: "4px",
                        }}
                      >
                        <ConfigFormLabel label={"SIS teacher username field"} tooltip={"The field to use as the username for teachers."} />
                        <Select
                          value={value || ""}
                          onChange={(e) => onChange(e.target.value)}
                          data-testid={"teacherUsernameKey-select"}
                          iconSize={"0"}
                          size="lg"
                          variant={"outline"}
                          sx={{
                            width: "100%",
                            height: "40px",
                            backgroundColor: "white",
                            borderRadius: "6px",
                            border: `1px solid ${colors.neutrals["30"]}`,
                            paddingLeft: "16px",
                          }}
                        >
                          <option value="">None (Optional)</option>
                          <option value="username">username</option>
                          <option value="active_directory_username">active_directory_username</option>
                        </Select>
                        <FormErrorMessage>{error && error.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  />
                  <Controller
                    control={control}
                    name="email_key"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <FormControl
                        isInvalid={!!error}
                        sx={{
                          display: "flex",
                          flexDir: "column",
                          minHeight: "40px",
                          gap: "4px",
                        }}
                      >
                        <ConfigFormLabel label={"SIS teacher email field"} tooltip={"The field to use as the email for teachers."} />
                        <Select
                          value={value || ""}
                          onChange={(e) => onChange(e.target.value)}
                          data-testid={"teacherEmailKey-select"}
                          iconSize={"0"}
                          size="lg"
                          variant={"outline"}
                          sx={{
                            width: "100%",
                            height: "40px",
                            backgroundColor: "white",
                            borderRadius: "6px",
                            border: `1px solid ${colors.neutrals["30"]}`,
                            paddingLeft: "16px",
                          }}
                        >
                          <option value="work">work</option>
                          <option value="email">email</option>
                          <option value="primary">primary</option>
                          <option value="home">home</option>
                        </Select>
                        <FormErrorMessage>{error && error.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  />
                </Box>
              )}

              <CheckBoxControl
                fieldProps={{
                  name: "sync_parent_data",
                  label: "Sync Parent Data",
                  tooltip: "When this is ticked, syncs include parent/guardian data",
                  control: control,
                  errors: errors,
                  rules: {
                    validate: (v: boolean) =>
                      v || syncClassroomDataWatch || "Please select either parent data, classroom data, or both to be synced.",
                  },
                }}
              />

              {syncParentDataWatch && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                    paddingLeft: "20px",
                  }}
                >
                  <Controller
                    control={control}
                    name="guardian_email_key"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <FormControl
                        isInvalid={!!error}
                        sx={{
                          display: "flex",
                          flexDir: "column",
                          minHeight: "40px",
                          gap: "4px",
                        }}
                      >
                        <ConfigFormLabel label={"SIS guardian email field"} tooltip={"The field to use as the email for guardians."} />
                        <Select
                          value={value || ""}
                          onChange={(e) => onChange(e.target.value)}
                          data-testid={"guardianEmailKey-select"}
                          iconSize={"0"}
                          size="lg"
                          variant={"outline"}
                          sx={{
                            width: "100%",
                            height: "40px",
                            backgroundColor: "white",
                            borderRadius: "6px",
                            border: `1px solid ${colors.neutrals["30"]}`,
                            paddingLeft: "16px",
                          }}
                        >
                          <option value="work">work</option>
                          <option value="email">email</option>
                          <option value="primary">primary</option>
                          <option value="home">home</option>
                        </Select>
                        <FormErrorMessage>{error && error.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  />
                  <Controller
                    control={control}
                    name="guardian_types"
                    rules={{
                      validate: (vals) =>
                        !syncParentDataWatch || (Array.isArray(vals) && vals.length > 0) || "Required at least one guardian type",
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <FormControl
                        isInvalid={!!error}
                        sx={{
                          display: "flex",
                          flexDir: "column",
                          minHeight: "40px",
                          gap: "4px",
                        }}
                      >
                        <ConfigFormLabel
                          label={"Select contact type"}
                          tooltip={"Please select at least one contact type to sync parent data"}
                        />
                        <NuMultiSelect
                          allItems={[{ value: "Lives with Pupil" }, { value: "Parental Responsibility" }, { value: "Correspondence" }]}
                          selectedValues={value || []}
                          displayTextLens={get("value")}
                          valueLens={get("value")}
                          onAdd={(item: { value: string }) => {
                            const v = get("value")(item);
                            const current = Array.isArray(value) ? value : [];
                            if (!current.includes(v)) {
                              onChange([...current, v]);
                            }
                          }}
                          onRemove={(val: string) => {
                            const current = Array.isArray(value) ? value : [];
                            onChange(current.filter((x) => x !== val));
                          }}
                          data-testid={"syncParentTypes-multiselect"}
                        />
                        <FormErrorMessage>{error && error.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  />
                </Box>
              )}

              <AttributeConfig clearErrors={clearErrors} syncYearAttributes={syncYearAttributesWatch} errors={errors} control={control} />

              <MergeOptions errors={errors} register={register} control={control} mergeKey={mergeKey} />
            </Box>
          </ModalBody>

          <ModalFooter
            sx={{
              display: "flex",
              flexDirection: "column-reverse",
              justifyContent: "space-between",
              gap: "0.5rem",
            }}
          >
            <Button
              data-testid={"submitBtn"}
              mt={4}
              disabled={!isDirty || isSubmitting}
              colorScheme="teal"
              type="submit"
              variant={"primary"}
            >
              Submit
            </Button>
            {multipleSyncsConfigured && (
              <InlineNotification
                status="warning"
                notificationTitle={"Important"}
                notificationDescription={
                  "Multiple SIS Integrations are configured. If multiple syncs are required, ensure they do not duplicate data such as Students, Guardians, Schools or Classrooms."
                }
              />
            )}
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};
